import { Box, Flex, Text, Button, VStack, } from "@chakra-ui/react";

import { OrderDto } from "doxecure-commons/doxecureClientTypes";
import { PaymentStatus } from "doxecure-commons/doxecureTypes";
import { $count, $ok } from "foundation-ts/commons";
import { Nullable, UUID } from "foundation-ts/types";

import { G1Icon } from "@/utils/icons";
import { formatDate } from "./helpers";
import { Card } from "./UI";

interface OrdersCardProps {
    orders?: Nullable<OrderDto[]>;
    onDownloadInvoice: (orderId: UUID, invoiceReference: string) => void;
}

export function OrdersCard(props: OrdersCardProps) {
    const { orders, onDownloadInvoice } = props;
    
    const countOrders = $count(orders);

    return (
        <Card gridColumn={{ lg: "span 2" }} overflow="hidden">
            <Flex justify="space-between" align="center" mb={5}>
                <Text fontFamily="heading" fontSize="lg" color="gray.900">Commandes</Text>
                <Text fontSize="xs" color="gray.400">
                    {countOrders} document{countOrders > 1 ? "s" : ""}
                </Text>
            </Flex>

            <Box overflowX="auto" mx={-6} px={6} visibility={countOrders > 0 ? "visible" : "hidden"}>
                <Box minW="500px">
                    <Flex px={3} pb={2}>
                        <Box flex="2" minW="110px">
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">N° facture</Text>
                        </Box>
                        <Box flex="3" minW="130px">
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">Description</Text>
                        </Box>
                        <Box w="76px" textAlign="right">
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">Montant</Text>
                        </Box>
                        <Box w="86px" textAlign="center">
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">Statut</Text>
                        </Box>
                        <Box w="60px" />
                    </Flex>

                    <VStack spacing={1} align="stretch">
                        {orders?.map((o, i) => {
                            const s = STATUS_STYLES[o.payment?.status ?? "pending"];
                            return (
                                <Flex key={o.apid} align="center" px={3} py={3} borderRadius="12px"
                                      bg={i % 2 === 0 ? "surface.subtle" : "white"}
                                      _hover={{ bg: "brand.50" }} transition="background 0.15s">
                                    <Box flex="2" minW="110px">
                                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>{o.invoiceReference ?? o.orderReference}</Text>
                                        <Text fontSize="xs" color="gray.400">{formatDate(o.payment?.date ?? o.orderDate)}</Text>
                                    </Box>
                                    <Box flex="3" minW="130px">
                                        <Text fontSize="sm" color="gray.500" noOfLines={1}>{o.description}</Text>
                                    </Box>
                                    <Box w="76px" textAlign="right">
                                        <Text fontSize="sm" fontWeight="700" color="gray.900">{o.totalPriceTTC}</Text>
                                    </Box>
                                    <Flex w="86px" justify="center">
                                        <Box px={2} py="2px" borderRadius="full" bg={s.bg}>
                                            <Text fontSize="xs" fontWeight="600" color={s.color} whiteSpace="nowrap">{s.label}</Text>
                                        </Box>
                                    </Flex>
                                    <Flex w="60px" justify="flex-end">
                                        <Button size="xs" variant="ghost" color="brand.500" _hover={{ bg: "brand.50" }} leftIcon={<G1Icon.InvoiceDownload />}
                                                isDisabled={!$ok(o.invoiceReference)} onClick={() => onDownloadInvoice(o.apid, o.invoiceReference!)}>
                                            PDF
                                        </Button>
                                    </Flex>
                                </Flex>
                            );
                        })}
                    </VStack>
                </Box>
            </Box>
        </Card>
    );
}

const STATUS_STYLES: Record<PaymentStatus, { label: string; bg: string; color: string }> = {
    paid:       { label: "Payée",      bg: "#f0fdf4", color: "#15803d" },
    pending:    { label: "En attente", bg: "#fffbeb", color: "#b45309" },
    failed:     { label: "Échouée",    bg: "#fef2f2", color: "#dc2626" },
    canceled:   { label: "Annulée",    bg: "#fef2f2", color: "#dc2626" },
    expired:    { label: "Expirée",    bg: "#fef2f2", color: "#dc2626" },
    open:       { label: "En cours",   bg: "#fffbeb", color: "#b45309" }, // Do not have to show
    authorized: { label: "En cours",   bg: "#fffbeb", color: "#b45309" }, // Do not have to show
};